import React, { useCallback, useEffect, useMemo, useState } from "react";

// components
import { Text } from "@performyard/py-components";
// types
import {
	EngagementCohortInsightResponseStreamJson,
	MetaDataEngagementCohortInsightResponseStreamJson,
	UserErrorResponse,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { ICohortFactorSummaryPoint, ISummaryRequestParams } from "../../../pages/Engagement/schema/defs/common";

// hooks
import { useAiQuery } from "../../../api/hooks/defs/ai";

// utils
import { determineDateRange } from "../utils";

import { InsightsGeneratedAt } from "../InsightsGeneratedAt";
import { AISidePanel } from "../SidePanel";

type EngagementCohortsInsightModuleProps = {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	cohortData: {
		_id: string;
		name: string | null;
		description: string | null;
	} | null;
	factorsData: ICohortFactorSummaryPoint[];
	filter: ISummaryRequestParams & { value?: string };
};

const MIN_FACTORS_FOR_INSIGHT = 3;

const INSUFFICIENT_DATA_MESSAGE =
	"Insights are not available for this cohort. At least three satisfaction factors must have a score before insights can be generated.";

const GENERIC_INSIGHT_ERROR_MESSAGE =
	"We couldn't generate insights for this cohort right now. Please try again, or contact support if this keeps happening.";

// prepares cohort factors data for insight
const getCohortFactorsData = (
	factorsData: ICohortFactorSummaryPoint[],
	cohortData: EngagementCohortsInsightModuleProps["cohortData"]
) => {
	const factorsLowestToHighest: ICohortFactorSummaryPoint[] = [];
	const factorsDataForCohort = factorsData
		.filter((factorData) => factorData.cohort === (cohortData?._id || "all") && factorData.factor !== "eNPS")
		.sort((a, b) => (a.average ?? 0) - (b.average ?? 0));

	// Only factors with an actual score can be ranked lowest/highest; a null score isn't a real "lowest".
	const scoredFactors = factorsDataForCohort.filter((factorData) => factorData.average != null);

	if (scoredFactors.length >= MIN_FACTORS_FOR_INSIGHT) {
		factorsLowestToHighest.push({
			...scoredFactors[0],
			average: scoredFactors[0].average
				? parseFloat(scoredFactors[0].average.toFixed(1))
				: scoredFactors[0].average,
		});
		factorsLowestToHighest.push({
			...scoredFactors[1],
			average: scoredFactors[1].average
				? parseFloat(scoredFactors[1].average.toFixed(1))
				: scoredFactors[1].average,
		});
		const lastFactor = scoredFactors[scoredFactors.length - 1];
		factorsLowestToHighest.push({
			...lastFactor,
			average: lastFactor.average ? parseFloat(lastFactor.average.toFixed(1)) : lastFactor.average,
		});
	}

	// Calculate satisfaction using ALL factors (excluding eNPS)
	const satisfactionScore = parseFloat(
		factorsDataForCohort
			.reduce((accum, curr) => accum + (curr.average ?? 0) / factorsDataForCohort.length, 0)
			.toFixed(1)
	);

	const enpsScore = factorsData.find(
		(factorData) => factorData.cohort === (cohortData?._id || "all") && factorData.factor === "eNPS"
	)?.eNPS_score;

	return {
		_id: cohortData?._id || null,
		name: cohortData?.name || null,
		description: cohortData?.description || null,
		satisfaction: satisfactionScore,
		enps: enpsScore ?? 0,
		factorsLowestToHighest,
	};
};

// prepares company factors data for insight
const getCompanyFactorsData = (
	factorsData: ICohortFactorSummaryPoint[],
	cohortFactorsData: ReturnType<typeof getCohortFactorsData>
) => {
	const companyFactors = factorsData.filter((factorData) => factorData.cohort === "all");
	const companyEnps = companyFactors.find((factorData) => factorData.factor === "eNPS")?.eNPS_score;

	// Calculate company satisfaction using ALL factors (excluding eNPS), not just the filtered ones
	const companyFactorsForSatisfaction = companyFactors.filter((factorData) => factorData.factor !== "eNPS");

	const companySatisfaction = parseFloat(
		companyFactorsForSatisfaction
			.reduce((accum, curr) => accum + (curr.average ?? 0) / companyFactorsForSatisfaction.length, 0)
			.toFixed(1)
	);

	// Still filter factors for display purposes (to show comparison with cohort's selected factors)
	const companyFactorsFiltered = companyFactors
		.filter((curr) =>
			cohortFactorsData.factorsLowestToHighest.some((factorData) => factorData.factor === curr.factor)
		)
		.map((factor) => ({
			...factor,
			average: factor.average ? parseFloat(factor.average.toFixed(1)) : factor.average,
		}));

	return {
		enps: companyEnps ?? 0,
		satisfaction: companySatisfaction,
		factors: companyFactorsFiltered,
	};
};

const prepareDataForInsight = (
	factorsData: ICohortFactorSummaryPoint[],
	cohortData: EngagementCohortsInsightModuleProps["cohortData"]
) => {
	const cohortFactorsData = getCohortFactorsData(factorsData, cohortData);
	const companyFactorsData = getCompanyFactorsData(factorsData, cohortFactorsData);

	return {
		cohortFactorsData,
		companyFactorsData,
	};
};

export const EngagementCohortsInsightModule = ({
	isOpen,
	onOpenChange,
	cohortData,
	factorsData,
	filter,
}: EngagementCohortsInsightModuleProps) => {
	const [recreateCount, setRecreateCount] = useState(0);
	const [retryIsProcessing, setRetryIsProcessing] = useState(false);

	const { companyFactorsData, cohortFactorsData } = useMemo(() => {
		return prepareDataForInsight(factorsData, cohortData);
	}, [factorsData, cohortData]);

	const dateRange = useMemo(
		() => determineDateRange(filter.startDate || undefined, filter.endDate || undefined, filter.value || ""),
		[filter.startDate, filter.endDate, filter.value]
	);

	const isValidCohortForInsight = useMemo(() => {
		// Count THIS cohort's non-eNPS factors that actually have a score — matches what the
		// backend can summarize and the product copy ("at least three factors must have a score").
		const cohortId = cohortData?._id || "all";
		const scoredFactorCount = factorsData.filter(
			(factorData: ICohortFactorSummaryPoint) =>
				factorData.cohort === cohortId && factorData.factor !== "eNPS" && factorData.average != null
		).length;
		return scoredFactorCount >= MIN_FACTORS_FOR_INSIGHT;
	}, [factorsData, cohortData]);

	const { data, isLoading, isFetching, isRefetching } = useAiQuery<
		MetaDataEngagementCohortInsightResponseStreamJson | UserErrorResponse
	>({
		type: "engagement-cohort-insights",
		dateRange,
		companyFactorsData,
		cohortFactorsData,
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
		enabled: isValidCohortForInsight,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching || retryIsProcessing) return true;
		if (data && typeof data === "object" && "status" in data) {
			return data.status === "in_progress";
		}
		return false;
	}, [data, isLoading, isRefetching, retryIsProcessing]);

	const isError = (
		response: UserErrorResponseJson | MetaDataEngagementCohortInsightResponseStreamJson | undefined
	): response is UserErrorResponseJson => {
		if (!response || inProgress || retryIsProcessing) return false;

		// if response is type MetaDataEngagementCohortInsightResponseStreamJson then return false
		if (response && typeof response === "object" && "status" in response) {
			return false;
		}

		if (
			response &&
			typeof response === "object" &&
			"data" in response &&
			typeof (response as { data?: unknown }).data === "object" &&
			response.data !== null &&
			"message" in (response.data as Record<string, unknown>)
		) {
			console.error(response);
			(response.data as UserErrorResponse).message = INSUFFICIENT_DATA_MESSAGE;
			return true;
		}

		// if response is type UserErrorResponse then return true
		if (!isValidCohortForInsight) {
			response.message = INSUFFICIENT_DATA_MESSAGE;
			return true;
		}

		// Valid cohort but the request still errored — show a neutral message, never a raw
		// server string (e.g. the cross-feature "Unable to process review").
		response.message = GENERIC_INSIGHT_ERROR_MESSAGE;
		return true;
	};

	const handleRetry = useCallback(() => {
		setRecreateCount((prev) => prev + 1);
		setRetryIsProcessing(true);
	}, []);

	useEffect(() => {
		if (!isFetching && retryIsProcessing) {
			setRetryIsProcessing(false);
		}
	}, [isFetching, retryIsProcessing]);

	const renderHeader = (data: EngagementCohortInsightResponseStreamJson | UserErrorResponse) => {
		// If it's an error response, don't render the header
		if ("message" in data) {
			return null;
		}

		return (
			<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
				<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
					<div className="flex min-w-0 flex-1 flex-col items-start gap-2">
						<Text size="paragraph-2" weight="semi-bold">
							{cohortData?.name || "All Employees"}
						</Text>
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Data Range:{" "}
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								{dateRange}
							</Text>
						</Text>
					</div>
				</div>
			</div>
		);
	};

	const renderInfoSection = (insightData: EngagementCohortInsightResponseStreamJson): React.ReactNode => (
		<InsightsGeneratedAt label="Insights Populated" date={insightData.createdAt} />
	);

	// Resolve the displayed error once — isError() both classifies and sets the user-safe message.
	const errorToShow = !isValidCohortForInsight ? { message: INSUFFICIENT_DATA_MESSAGE } : isError(data) ? data : null;

	return (
		<AISidePanel<MetaDataEngagementCohortInsightResponseStreamJson>
			type="engagement"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={errorToShow || !data ? null : (data as MetaDataEngagementCohortInsightResponseStreamJson)}
			isLoading={inProgress}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			onRetry={handleRetry}
			error={errorToShow}
			logoLoaderProps={{
				textArray: [
					"Compiling Scores…",
					"Organizing Scores…",
					"Retrieving Relevant Questions…",
					"Evaluating Scores…",
					"Generating Analysis…",
					"Structuring Analysis Format…",
					"Finalizing Results..",
					"Finishing Up…",
				],
				mainText: "Summarizing Scores",
			}}
		/>
	);
};
